import { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import type { OverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getSpecialGameModeChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { ArenaSearch } from "./ArenaSearch";
import { ArenaModeType } from "@ugg/shared/utils/queue-type-helpers";
import { getArenaSynergyWinRateColor } from "@ugg/shared/utils/win-rates";

interface ArenaSynergiesProps {
  className?: string;
  championId: number;
  data: OverviewInfo["arena_synergies"];
  patch?: string;
  arenaModeType: ArenaModeType;
}
export function ArenaSynergies(props: ArenaSynergiesProps) {
  const { className, championId, data, patch, arenaModeType } = props;
  const [searchValue, setSearchValue] = useState("");
  const [scrollPadding, setScrollPadding] = useState(data.length >= 5 ? 14 : 0);
  const scrollable = useRef<HTMLDivElement>(null);
  const { getChampionName, getChampionImgFromSprite, getNormalizedChampionName } = getRiotAssetsContext();
  const [currentSortable, setCurrentSortable] = useState<{ id: keyof (typeof data)[0]; sort: "asc" | "desc" }>({
    id: "matches",
    sort: "desc",
  });

  const setSortable = (id: (typeof currentSortable)["id"], sort: (typeof currentSortable)["sort"] = "desc") => {
    if (currentSortable.id === id) {
      if (currentSortable.sort === "asc") setCurrentSortable({ id, sort: "desc" });
      else if (currentSortable.sort === "desc") setCurrentSortable({ id, sort: "asc" });
    } else {
      setCurrentSortable({ id, sort });
    }
  };

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const sortedReference = useMemo(() => {
    let lookup: { [key: number]: number } = {};
    data
      .sort((a, b) => {
        if (currentSortable.sort === "asc") {
          return a[currentSortable.id] - b[currentSortable.id];
        } else {
          return b[currentSortable.id] - a[currentSortable.id];
        }
      })
      .forEach((champion, index) => {
        lookup[champion.championId] = index;
      });
    return lookup;
  }, [data, currentSortable]);

  const sortedData = useMemo(() => {
    return data
      .filter((champion) => {
        const championName = getChampionName(champion.championId);
        const normalizedChampionName = getNormalizedChampionName(champion.championId);
        const filterValue = searchValue.toLowerCase().replace(/ /g, "");

        if (!filterValue) return true;
        return (
          championName.toLowerCase().indexOf(filterValue) === 0 || normalizedChampionName.toLowerCase().indexOf(filterValue) === 0
        );
      })
      .sort((a, b) => {
        if (currentSortable.sort === "asc") {
          return a[currentSortable.id] - b[currentSortable.id];
        } else {
          return b[currentSortable.id] - a[currentSortable.id];
        }
      });
  }, [data, searchValue, currentSortable]);

  useEffect(() => {
    setScrollPadding((scrollable.current?.offsetWidth || 0) - (scrollable.current?.clientWidth || 0));
  }, []);

  const shouldApplyScrollPadding = data.length > 5 && sortedData.length <= 5;

  return (
    <div className={classnames("arena-synergies bg-purple-400", className)}>
      <div className="relative flex items-center justify-between leading-[20px] pl-[18px] pr-[18px] max-xxs:pl-[12px] h-[56px]">
        <HeaderBullet>
          <div className="flex-1">{getChampionName(championId)} Synergies</div>
        </HeaderBullet>
        <ArenaSearch className="w-[170px]" placeholder="Search Champion" value={searchValue} onChange={searchOnChange} />
      </div>
      <div className="synergies-table text-[12px] font-medium">
        <div
          style={{ paddingRight: scrollPadding }}
          className="table_header flex max-xxs:pl-[12px] pl-[18px] pt-[12px] pb-[12px] border-t-[1px] border-b-[1px] border-purple-500 bg-purple-100"
        >
          <div className="flex-1 text-lavender-50 max-xxs:invisible">Champion</div>
          <div
            className={classnames("flex items-center basis-[80px] justify-center text-lavender-50 cursor-pointer", {
              "font-bold !text-white": currentSortable.id === "avgPlacement",
            })}
            onClick={() => setSortable("avgPlacement")}
          >
            Avg. Place
          </div>
          <div
            className={classnames("flex items-center basis-[80px] justify-center text-lavender-50 cursor-pointer", {
              "font-bold !text-white": currentSortable.id === "firstRate",
            })}
            onClick={() => setSortable("firstRate")}
          >
            1st Rate
          </div>
          <div
            className={classnames("flex items-center basis-[80px] justify-center text-lavender-50 cursor-pointer", {
              "font-bold !text-white": currentSortable.id === "top2Rate",
            })}
            onClick={() => setSortable("top2Rate")}
          >
            Top {arenaModeType === 16 ? 4 : 2} Rate
          </div>
          <div
            className={classnames("flex items-center basis-[80px] justify-center text-lavender-50 cursor-pointer", {
              "font-bold !text-white": currentSortable.id === "matches",
            })}
            onClick={() => setSortable("matches")}
          >
            Pick Rate
          </div>
        </div>
        <div ref={scrollable} className="relative h-[230px] overflow-y-auto">
          <div
            className="absolute left-0 right-0 flex flex-col gap-[16px] pt-[8px] pb-[8px]"
            style={{ paddingRight: shouldApplyScrollPadding ? scrollPadding : 0 }}
          >
            {sortedData.map((champion, index) => {
              const { championId, avgPlacement, firstRate, top2Rate, pickRate, matches } = champion;
              const normalizedChampionName = getNormalizedChampionName(championId);

              return (
                <div key={champion.championId} className="flex">
                  <div className="flex flex-1 items-center pl-[18px] max-xxs:pl-[8px]">
                    <div className="mr-[8px] w-[16px] text-center text-lavender-50">{sortedReference[championId] + 1}</div>
                    <Link
                      className="flex items-center hover:underline"
                      to={{
                        pathname: getSpecialGameModeChampionBuildUrl(normalizedChampionName, "arena"),
                        search: patch ? `patch=${patch}` : "",
                      }}
                    >
                      <div className="relative w-[30px] h-[30px] rounded-[2px] overflow-hidden">
                        <div className="absolute scale-[1.1]">{getChampionImgFromSprite(champion.championId, { size: 30 })}</div>
                      </div>
                      <span className="ml-[12px] font-bold max-lg:hidden">{getChampionName(champion.championId)}</span>
                    </Link>
                  </div>
                  <div
                    className={classnames("flex items-center basis-[80px] justify-center text-lavender-50", {
                      "font-bold text-white": currentSortable.id === "avgPlacement",
                    })}
                  >
                    {avgPlacement.toFixed(2)}
                  </div>
                  <div
                    className={classnames("flex items-center basis-[80px] justify-center text-lavender-50", {
                      "font-bold text-white": currentSortable.id === "firstRate",
                    })}
                  >
                    {firstRate.toFixed(2)}%
                  </div>
                  <div
                    style={{ color: getArenaSynergyWinRateColor(top2Rate) }}
                    className={classnames("flex items-center basis-[80px] justify-center text-lavender-50", {
                      "font-bold text-white": currentSortable.id === "top2Rate",
                    })}
                  >
                    {top2Rate.toFixed(2)}%
                  </div>
                  <div className="flex flex-col items-center basis-[80px] justify-center text-lavender-50">
                    <span className={classnames("text-white leading-none", { "font-bold": currentSortable.id === "matches" })}>
                      {pickRate.toFixed(2)}%
                    </span>
                    <span className="text-[11px]">{numberWithCommas(matches)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
